/**
 * A user-agent string, said out loud.
 *
 * The session list is only useful if you can recognise your own devices in it. Nobody recognises
 * `Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 …` as their laptop, so a
 * raw list of those is a list you cannot act on: the question it exists to answer is "is one of
 * these not me", and every row looking equally like machine noise means the answer is always
 * "no idea".
 *
 * Deliberately shallow. Every user-agent string is a pile of compatibility lies — Chrome claims
 * to be Safari, which claims to be Gecko, which claims to be Mozilla — and a parser that tries to
 * be exhaustive gets the common cases wrong while chasing the rare ones. Order matters below for
 * exactly that reason: the more specific claim is tested before the one it also contains.
 */

/**
 * Browser families, most specific first — Edge says "Chrome", Chrome says "Safari".
 *
 * `HeadlessChrome` earns its own entry rather than being folded into Chrome, and it is why this
 * list was wrong: `\bChrome\/` cannot match `HeadlessChrome/` because there is no word boundary
 * between `s` and `C`, so every headless session fell through to `Safari/` — which every
 * Chromium UA also carries — and twenty-nine rows all read "Safari on Linux". A confident wrong
 * answer on the one screen whose job is "is one of these not me".
 *
 * Worth telling apart, too: a headless browser in your session list is a script, not a person.
 */
const BROWSERS: [RegExp, string][] = [
  [/\bEdg[A-Z]?\//, 'Edge'],
  [/\bOPR\/|\bOpera\b/, 'Opera'],
  [/\bFirefox\/|\bFxiOS\//, 'Firefox'],
  [/HeadlessChrome\//, 'Headless Chrome'],
  [/\bChrome\/|\bCriOS\//, 'Chrome'],
  [/\bSafari\//, 'Safari'],
];

/** Platforms, most specific first — iPadOS says "Mac OS X", Android says "Linux". */
const PLATFORMS: [RegExp, string][] = [
  [/\biPhone\b/, 'iPhone'],
  [/\biPad\b/, 'iPad'],
  [/\bAndroid\b/, 'Android'],
  [/\bWindows\b/, 'Windows'],
  [/\bMac OS X\b|\bMacintosh\b/, 'Mac'],
  [/\bCrOS\b/, 'ChromeOS'],
  [/\bLinux\b/, 'Linux'],
];

/**
 * "Chrome on Mac", or as much of it as the string supports.
 *
 * Returns null rather than a guess when nothing matches — an honest "unrecognised device" is more
 * use than a confident wrong label on the one row you are scanning for something unfamiliar.
 */
export function describeDevice(userAgent: string | null): string | null {
  if (userAgent === null || userAgent.trim() === '') return null;

  const browser = BROWSERS.find(([re]) => re.test(userAgent))?.[1] ?? null;
  const platform = PLATFORMS.find(([re]) => re.test(userAgent))?.[1] ?? null;

  if (browser && platform) return `${browser} on ${platform}`;
  return browser ?? platform;
}

/**
 * How long ago, roughly.
 *
 * "3 days ago" answers "is this still me?" at a glance where a timestamp makes you subtract. Only
 * ever coarse: the precise instant is on the row as a tooltip for anyone who wants it.
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const seconds = Math.round((now.getTime() - new Date(iso).getTime()) / 1000);
  if (seconds < 90) return 'just now';

  const steps: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'minute'],
    [3600, 'hour'],
    [86_400, 'day'],
    [604_800, 'week'],
    [2_629_800, 'month'],
    [31_557_600, 'year'],
  ];
  // The largest unit that still gives a count of at least one.
  let chosen = steps[0]!;
  for (const step of steps) if (Math.abs(seconds) >= step[0]) chosen = step;

  const value = Math.round(seconds / chosen[0]);
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-value, chosen[1]);
}
